import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useSpaceNotificationsDispatch } from "@circle-react/contexts/sidebarNotificationsContext";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import {
  reactQueryGet,
  reactQueryPost,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useCallback, useEffect, useEffectEvent } from "react";
import { useMutation, useQuery } from "react-query";

export const useSpaceSidebarNotifications = (spaceIds = []) => {
  const dispatch = useSpaceNotificationsDispatch();
  const { currentCommunityMember } = usePunditUserContext();
  const canFetchSpaceNotifications = !!currentCommunityMember;

  const fetchRequest = () =>
    reactQueryGet(
      internalApi.sidebarNotifications.index({ space_ids: spaceIds }),
    );

  const {
    data: spaces,
    isInitialLoading,
    refetch,
  } = useQuery({
    queryKey: ["space-sidebar-notifications", spaceIds],
    queryFn: fetchRequest,
    enabled: false,
  });

  const applySpaces = useEffectEvent((nextSpaces = []) => {
    if (nextSpaces.length === 1) {
      dispatch({
        type: "UPDATE_SPACE_NOTIFICATIONS",
        payload: { space: nextSpaces[0] },
      });
      return;
    }
    dispatch({
      type: "SET_SPACE_NOTIFICATIONS",
      payload: { spaces: nextSpaces || [] },
    });
  });

  useEffect(() => {
    if (!spaces) return;
    applySpaces(spaces);
  }, [spaces]);

  const refetchSpaceNotifications = useCallback(() => {
    if (!canFetchSpaceNotifications) {
      return Promise.resolve();
    }

    return refetch();
  }, [canFetchSpaceNotifications, refetch]);

  return { refetchSpaceNotifications, isLoading: isInitialLoading };
};

export const useResetSpaceSidebarNotifications = spaceId => {
  const spacesContext = useSpacesContext();
  const { refetchSpaceNotifications } = useSpaceSidebarNotifications([spaceId]);

  const { mutateAsync: resetUnreadCount } = useMutation({
    mutationFn: () =>
      reactQueryPost(internalApi.spaces.resetUnreadCount({ spaceId }), {
        forReactQuery: true,
      }),
    onSuccess: updatedSpace => {
      spacesContext.actions.updateSpace(updatedSpace);
      refetchSpaceNotifications();
    },
    onError: err => {
      console.error("Error while resetting the unread count", err);
    },
  });

  return { resetUnreadCount };
};
